import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  flushRedisCache,
  getAppSettings,
  updateAppSettings,
} from "../../apis/app.api";
import { AppAlertBox } from "../../components/modals/alert-box";
import MuiTabs from "../../components/mui/tabs";
import { AppTextField } from "../../components/textfields";
import { useDebounce } from "../../hooks/useDebounce";
import { showNotification } from "../../services/utils";
import { BusinessHoursRangeSlider, DEFAULT_BUSINESS_HOURS_RANGE } from "./business-hours-range-slider";
import { HolidaySetup } from "./holiday-setup";
import { useStyles } from "./styles";

type TopLevelNumericField = "maxInvitationsPerDay" | "incompleteProfileEmailAfterDays";

interface SlotRulesForm {
  minDaysAhead: string;
  allowWeekends: boolean;
  businessHours: {
    minHour: string;
    maxHour: string;
  };
}

interface AppSettingsForm {
  maxInvitationsPerDay: string;
  incompleteProfileEmailAfterDays: string;
  slotRules: SlotRulesForm;
}

interface FieldConfig {
  label: string;
  placeholder: string;
  helperText: string;
  requiredMessage: string;
}

const APP_SETTINGS_TABS = [
  { value: "holiday_setup", label: "Holiday Setup" },
  { value: "invitations_profiles", label: "Invitations and profiles" },
  { value: "slot_scheduling", label: "Slot scheduling" },
  { value: "system_maintenance", label: "System maintenance" },
];

const TOP_LEVEL_FIELD_CONFIG: Record<TopLevelNumericField, FieldConfig> = {
  maxInvitationsPerDay: {
    label: "Maximum Invitations Per Day",
    placeholder: "Enter maximum number of invitations allowed per day",
    helperText:
      "Set the maximum number of invitations that can be sent per day. This limit will be enforced across all users in the system.",
    requiredMessage: "Number of invitations per day is required",
  },
  incompleteProfileEmailAfterDays: {
    label: "Incomplete Profile Email After Days",
    placeholder: "Enter number of days",
    helperText:
      "Set the number of days after which an email will be sent to the user to complete their profile.",
    requiredMessage:
      "Number of days after which an email will be sent to the user to complete their profile is required",
  },
};

const TOP_LEVEL_FIELDS: TopLevelNumericField[] = [
  "maxInvitationsPerDay",
  "incompleteProfileEmailAfterDays",
];

const MIN_DAYS_SLOT_FIELD = {
  label: "Minimum Days Ahead for Slots",
  placeholder: "e.g. 4",
  helperText: "Minimum number of calendar days in the future before a slot can be scheduled.",
};

function emptyFormState(): AppSettingsForm {
  return {
    maxInvitationsPerDay: "",
    incompleteProfileEmailAfterDays: "",
    slotRules: {
      minDaysAhead: "",
      allowWeekends: false,
      businessHours: { minHour: "", maxHour: "" },
    },
  };
}

function emptyErrors() {
  return {
    maxInvitationsPerDay: "",
    incompleteProfileEmailAfterDays: "",
    slotRules: {
      minDaysAhead: "",
      businessHours: { minHour: "", maxHour: "" },
    },
  };
}

type FormErrors = ReturnType<typeof emptyErrors>;

type FieldName = "maxInvitationsPerDay" | "incompleteProfileEmailAfterDays";

interface FieldConfig {
  label: string;
  placeholder: string;
  helperText: string;
  requiredMessage: string;
}

const FIELD_CONFIG: Record<FieldName, FieldConfig> = {
  maxInvitationsPerDay: {
    label: "Maximum Invitations Per Day",
    placeholder: "Enter maximum number of invitations allowed per day",
    helperText: "Set the maximum number of invitations that can be sent per day. This limit will be enforced across all users in the system.",
    requiredMessage: "Number of invitations per day is required",
  },
  incompleteProfileEmailAfterDays: {
    label: "Incomplete Profile Email After Days",
    placeholder: "Enter number of days",
    helperText: "Set the number of days after which an email will be sent to the user to complete their profile.",
    requiredMessage: "Number of days after which an email will be sent to the user to complete their profile is required",
  },
};

const FIELD_NAMES: FieldName[] = ["maxInvitationsPerDay", "incompleteProfileEmailAfterDays"];

function AppSettings() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(APP_SETTINGS_TABS[0].value);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showClearCacheConfirm, setShowClearCacheConfirm] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);
  const [formData, setFormData] = useState<AppSettingsForm>(emptyFormState);
  const [errors, setErrors] = useState<FormErrors>(emptyErrors);
  const initialDataRef = useRef<AppSettingsForm>(emptyFormState());
  const isInitialLoadRef = useRef(true);

  const debouncedFormData = useDebounce(formData, 1000);

  const validateTopLevelNumber = useCallback((value: string, requiredMessage: string): string => {
    if (!value?.trim()) return requiredMessage;
    const numValue = parseInt(value, 10);
    return isNaN(numValue) || numValue < 1 ? "Please enter a valid positive number" : "";
  }, []);

  const validateMinDaysAhead = useCallback((value: string): string => {
    if (!value?.trim()) return "Minimum days ahead is required";
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) return "Please enter zero or a positive whole number";
    return "";
  }, []);

  const validateHour = useCallback((value: string, requiredMessage: string): string => {
    if (!value?.trim()) return requiredMessage;
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0 || numValue > 23) {
      return "Please enter an hour between 0 and 23";
    }
    return "";
  }, []);

  const validateForm = useCallback((data: AppSettingsForm): boolean => {
    const next: FormErrors = emptyErrors();

    TOP_LEVEL_FIELDS.forEach((fieldName) => {
      next[fieldName] = validateTopLevelNumber(
        data[fieldName],
        TOP_LEVEL_FIELD_CONFIG[fieldName].requiredMessage
      );
    });

    next.slotRules.minDaysAhead = validateMinDaysAhead(data.slotRules.minDaysAhead);

    next.slotRules.businessHours.minHour = validateHour(
      data.slotRules.businessHours.minHour,
      "Start hour is required"
    );
    next.slotRules.businessHours.maxHour = validateHour(
      data.slotRules.businessHours.maxHour,
      "End hour is required"
    );

    const minH = parseInt(data.slotRules.businessHours.minHour, 10);
    const maxH = parseInt(data.slotRules.businessHours.maxHour, 10);
    if (
      !next.slotRules.businessHours.minHour &&
      !next.slotRules.businessHours.maxHour &&
      !isNaN(minH) &&
      !isNaN(maxH) &&
      minH >= maxH
    ) {
      next.slotRules.businessHours.maxHour = "End hour must be greater than start hour";
    }

    setErrors(next);
    const hasError =
      Boolean(next.maxInvitationsPerDay) ||
      Boolean(next.incompleteProfileEmailAfterDays) ||
      Boolean(next.slotRules.minDaysAhead) ||
      Boolean(next.slotRules.businessHours.minHour) ||
      Boolean(next.slotRules.businessHours.maxHour);
    return !hasError;
  }, [validateTopLevelNumber, validateMinDaysAhead, validateHour]);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const res: any = await dispatch(getAppSettings());
      if (res.success && res.data) {
        const sr = res.data.slotRules || {};
        const bh = sr.businessHours || {};
        const loadedData: AppSettingsForm = {
          maxInvitationsPerDay: res.data.maxInvitationsPerDay?.toString() || "",
          incompleteProfileEmailAfterDays:
            res.data.incompleteProfileEmailAfterDays?.toString() || "",
          slotRules: {
            minDaysAhead: sr.minDaysAhead != null ? String(sr.minDaysAhead) : "",
            allowWeekends: Boolean(sr.allowWeekends),
            businessHours: {
              minHour:
                bh.minHour != null ? String(bh.minHour) : String(DEFAULT_BUSINESS_HOURS_RANGE[0]),
              maxHour:
                bh.maxHour != null ? String(bh.maxHour) : String(DEFAULT_BUSINESS_HOURS_RANGE[1]),
            },
          },
        };
        setFormData(loadedData);
        initialDataRef.current = loadedData;
        setErrors(emptyErrors());
      }
    } catch (err) {
      showNotification({ message: "Failed to load app settings", type: "error" });
    } finally {
      setIsLoading(false);
      isInitialLoadRef.current = false;
    }
  }, [dispatch]);

  const saveSettings = useCallback(
    async (data: AppSettingsForm) => {
      if (!validateForm(data)) return;

      setIsSaving(true);
      try {
        const payload = {
          maxInvitationsPerDay: parseInt(data.maxInvitationsPerDay, 10),
          incompleteProfileEmailAfterDays: parseInt(data.incompleteProfileEmailAfterDays, 10),
          slotRules: {
            minDaysAhead: parseInt(data.slotRules.minDaysAhead, 10),
            allowWeekends: data.slotRules.allowWeekends,
            businessHours: {
              minHour: parseInt(data.slotRules.businessHours.minHour, 10),
              maxHour: parseInt(data.slotRules.businessHours.maxHour, 10),
            },
          },
        };

        const res: any = await dispatch(updateAppSettings(payload));
        if (res.success) {
          initialDataRef.current = data;
        }
      } catch (err) {
        showNotification({ message: "Failed to save app settings", type: "error" });
      } finally {
        setIsSaving(false);
      }
    },
    [validateForm, dispatch]
  );

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (isInitialLoadRef.current) return;

    const hasChanges = !isEqual(debouncedFormData, initialDataRef.current);
    if (hasChanges) {
      saveSettings(debouncedFormData);
    }
  }, [debouncedFormData, saveSettings]);

  const handleTopLevelChange = useCallback((fieldName: TopLevelNumericField) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === "" || /^\d+$/.test(value)) {
        setFormData((prev) => ({ ...prev, [fieldName]: value }));
        setErrors((prev) => (prev[fieldName] ? { ...prev, [fieldName]: "" } : prev));
      }
    };
  }, []);

  const handleSlotMinDaysChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        slotRules: { ...prev.slotRules, minDaysAhead: value },
      }));
      setErrors((prev) =>
        prev.slotRules.minDaysAhead
          ? { ...prev, slotRules: { ...prev.slotRules, minDaysAhead: "" } }
          : prev
      );
    }
  }, []);

  const handleBusinessHoursRangeChange = useCallback((minH: string, maxH: string) => {
    setFormData((prev) => ({
      ...prev,
      slotRules: {
        ...prev.slotRules,
        businessHours: { minHour: minH, maxHour: maxH },
      },
    }));
    setErrors((prev) => ({
      ...prev,
      slotRules: {
        ...prev.slotRules,
        businessHours: { minHour: "", maxHour: "" },
      },
    }));
  }, []);

  const handleAllowWeekendsChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      slotRules: { ...prev.slotRules, allowWeekends: checked },
    }));
  }, []);

  const handleClearCacheClick = () => {
    setShowClearCacheConfirm(true);
  };

  const handleClearCacheCancel = () => {
    setShowClearCacheConfirm(false);
  };

  const handleClearCacheConfirm = async () => {
    setShowClearCacheConfirm(false);
    setIsClearingCache(true);

    const response: any = await dispatch(flushRedisCache());

    setIsClearingCache(false);

    if (!response?.success) {
      showNotification({
        message: response?.message || "Failed to clear cache. Please try again.",
        type: "error",
      });
    }
  };

  const disabled = isSaving || isLoading;

  const businessHoursError =
    errors.slotRules.businessHours.minHour || errors.slotRules.businessHours.maxHour;

  const renderTabContent = () => {
    if (selectedTab === "invitations_profiles") {
      return (
        <Paper
          variant="outlined"
          className={classes.categoryCard}
          component="section"
          aria-labelledby="app-settings-invitations-heading"
        >
          <Typography
            variant="h6"
            mb={2}
            component="h2"
            id="app-settings-invitations-heading"
            className={classes.cardTitle}
          >
            Invitations and profiles
          </Typography>
          <Grid container spacing={2}>
            {TOP_LEVEL_FIELDS.map((fieldName) => (
              <Grid item xs={12} md={6} key={fieldName}>
                <Box className={classes.formField}>
                  <AppTextField
                    label={TOP_LEVEL_FIELD_CONFIG[fieldName].label}
                    placeholder={TOP_LEVEL_FIELD_CONFIG[fieldName].placeholder}
                    value={formData[fieldName]}
                    onChange={handleTopLevelChange(fieldName)}
                    error={errors[fieldName]}
                    name={fieldName}
                    inputProps={{ min: 1, step: 1 }}
                    fullWidth
                    disabled={disabled}
                  />
                  <Typography variant="caption" className={classes.helperText}>
                    {TOP_LEVEL_FIELD_CONFIG[fieldName].helperText}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      );
    }

    if (selectedTab === "slot_scheduling") {
      return (
        <Paper
          variant="outlined"
          className={classes.categoryCard}
          component="section"
          aria-labelledby="app-settings-slots-heading"
        >
          <Typography
            variant="h6"
            mb={2}
            component="h2"
            id="app-settings-slots-heading"
            className={classes.cardTitle}
          >
            Slot scheduling
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BusinessHoursRangeSlider
                minHour={formData.slotRules.businessHours.minHour}
                maxHour={formData.slotRules.businessHours.maxHour}
                onChange={handleBusinessHoursRangeChange}
                disabled={disabled}
                error={businessHoursError}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.formField}>
                <AppTextField
                  label={MIN_DAYS_SLOT_FIELD.label}
                  placeholder={MIN_DAYS_SLOT_FIELD.placeholder}
                  value={formData.slotRules.minDaysAhead}
                  onChange={handleSlotMinDaysChange}
                  error={errors.slotRules.minDaysAhead}
                  name="slotRules.minDaysAhead"
                  inputProps={{ min: 0, step: 1 }}
                  fullWidth
                  disabled={disabled}
                />
                <Typography variant="caption" className={classes.helperText}>
                  {MIN_DAYS_SLOT_FIELD.helperText}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.slotRules.allowWeekends}
                    onChange={handleAllowWeekendsChange}
                    disabled={disabled}
                    color="primary"
                  />
                }
                label="Allow weekend slots"
              />
            </Grid>
          </Grid>
        </Paper>
      );
    }

    if (selectedTab === "system_maintenance") {
      return (
        <Paper
          variant="outlined"
          className={classes.categoryCard}
          component="section"
          aria-labelledby="app-settings-cache-heading"
        >
          <Typography
            variant="h6"
            mb={2}
            component="h2"
            id="app-settings-cache-heading"
            className={classes.cardTitle}
          >
            System maintenance
          </Typography>
          <Box className={classes.cacheField}>
            <Box className={classes.cacheActionRow}>
              <Button
                variant="outlined"
                color="primary"
                className={classes.cacheButton}
                onClick={handleClearCacheClick}
                disabled={isClearingCache || isLoading}
                sx={{ textTransform: "none" }}
              >
                {isClearingCache ? "Clearing cache…" : "Clear cache"}
              </Button>
            </Box>
          </Box>
        </Paper>
      );
    }

    if (selectedTab === "holiday_setup") {
      return <HolidaySetup />;
    }

    return null;
  };

  return (
    <Box className={classes.pageRoot} sx={{ width: 1 }}>
      <MuiTabs
        items={APP_SETTINGS_TABS}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        ariaLabel="App settings tabs"
      />

      <Box mt={3}>{renderTabContent()}</Box>

      <AppAlertBox
        isModalOpen={showClearCacheConfirm}
        handleClose={handleClearCacheCancel}
        onConfirm={handleClearCacheConfirm}
        onCancel={handleClearCacheCancel}
        message={
          <Box>
            <Typography fontWeight={600} mb={1.5}>
              Clear application cache?
            </Typography>
            <Typography fontSize="14px" color="#6B7280" lineHeight={1.6}>
              This will flush the Redis cache immediately. Only continue if you
              need to refresh cached application data.
            </Typography>
          </Box>
        }
        maxModalWidth="sm"
      />
    </Box>
  );
}

export { AppSettings };
